import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { Phone, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { mockData } from "../data/mockData";
import { submitContactForm } from "../services/api";

const ContactSection = ({ data }) => {
  const company = data?.companyInfo?.company || {
    phone: "0437 160 997",
    email: "hello@reignitehealth.com.au",
    address: "Serving retirement villages across NSW"
  };
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    village: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await submitContactForm(formData);
      
      toast({
        title: "Message Sent Successfully!",
        description: "We'll get back to you within 24 hours.",
      });
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        village: "",
        message: ""
      });
    } catch (error) {
      toast({
        title: "Error Sending Message",
        description: "Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-black">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-light mb-8">Let's work together</h2>
            <div className="w-24 h-px bg-red-500 mx-auto mb-8"></div>
            <p className="text-xl text-white/80 max-w-3xl mx-auto font-light">
              Ready to bring exceptional healthcare to your retirement village? 
              Drop us a message and let's discuss how we can help.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl md:text-3xl font-light mb-8">We're expanding...</h3>
                <p className="text-white/80 leading-relaxed font-light mb-8">
                  Please reach out if you would like Reignite Health at your retirement village, 
                  want to refer someone to us, or are interested in becoming part of our incredible 
                  team of physiotherapists and exercise physiologists!
                </p>
              </div>

              {/* Contact Details */}
              <div className="space-y-6">
                <div className="flex items-center group">
                  <div className="p-3 bg-red-500/10 rounded-lg mr-4 group-hover:bg-red-500/20 transition-all duration-300">
                    <Phone className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-white font-light">{company.phone}</p>
                    <p className="text-white/60 text-sm font-light">Call us anytime</p>
                  </div>
                </div>

                <div className="flex items-center group">
                  <div className="p-3 bg-red-500/10 rounded-lg mr-4 group-hover:bg-red-500/20 transition-all duration-300">
                    <Mail className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-white font-light">{company.email}</p>
                    <p className="text-white/60 text-sm font-light">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-center group">
                  <div className="p-3 bg-red-500/10 rounded-lg mr-4 group-hover:bg-red-500/20 transition-all duration-300">
                    <MapPin className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-white font-light">{company.address}</p>
                    <p className="text-white/60 text-sm font-light">State wide service coverage</p>
                  </div>
                </div>

                <div className="flex items-center group">
                  <div className="p-3 bg-red-500/10 rounded-lg mr-4 group-hover:bg-red-500/20 transition-all duration-300">
                    <Clock className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="text-white font-light">Monday - Friday</p>
                    <p className="text-white/60 text-sm font-light">8:00 AM - 6:00 PM AEST</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="bg-gray-900/50 border-white/10">
              <CardHeader>
                <CardTitle className="text-2xl font-light text-white">Send Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input
                        type="text"
                        name="name"
                        placeholder="Your Name*"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="bg-black/40 border-white/20 text-white placeholder:text-white/50 focus:border-red-500/50 focus:ring-red-500/25"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        name="email"
                        placeholder="Email*"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="bg-black/40 border-white/20 text-white placeholder:text-white/50 focus:border-red-500/50 focus:ring-red-500/25"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Input
                        type="tel"
                        name="phone"
                        placeholder="Phone Number*"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="bg-black/40 border-white/20 text-white placeholder:text-white/50 focus:border-red-500/50 focus:ring-red-500/25"
                      />
                    </div>
                    <div>
                      <Input
                        type="text"
                        name="village"
                        placeholder="Retirement Village Name"
                        value={formData.village}
                        onChange={handleInputChange}
                        className="bg-black/40 border-white/20 text-white placeholder:text-white/50 focus:border-red-500/50 focus:ring-red-500/25"
                      />
                    </div>
                  </div>

                  <div>
                    <Textarea
                      name="message"
                      placeholder="How can we help your retirement village?"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      className="bg-black/40 border-white/20 text-white placeholder:text-white/50 focus:border-red-500/50 focus:ring-red-500/25 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-500 hover:bg-red-600 disabled:bg-red-500/50 text-white py-3 font-light transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-red-500/25"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                    {!isSubmitting && <ArrowRight className="ml-2 w-4 h-4" />}
                  </Button>
                </form>

                <p className="text-xs text-white/40 mt-6 text-center font-light">
                  This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;